/**
 * Quick-filter chip URL rules — NGE-11501.
 *
 * Pure string→string, no DOM, so `npm test` can drive the toggle without a
 * browser. The element in `plp-filter-chips.js` only wires these to clicks.
 */

/**
 * Shopify represents a multi-select facet as the SAME key repeated once per
 * selected value (`filter.p.m.custom.color=red&filter.p.m.custom.color=blue`),
 * so a chip that owns one of those values must add and remove only its own
 * pair. These are the params that never repeat — a chip carrying one replaces
 * whatever is there.
 */
const SINGLE_VALUED = /^(sort_by|q|type|page)$/;

/**
 * Shopify's price range is two keys but one facet. A chip that sets one bound
 * has to clear the other, or `?filter.v.price.gte=50` plus an under-20 chip
 * asks for an empty range while the chip reads as selected.
 */
const PRICE_BOUND = /^filter\.v\.price\.(gte|lte)$/;

/**
 * A chip is active when every param it carries is already selected. For a
 * repeatable facet key that means "is among the selected values", not "is the
 * first one".
 *
 * A chip carrying no params can never be active — that would light it up on a
 * bare collection URL and on every filtered one alike.
 *
 * @param {string} chipQuery - the chip's own query string
 * @param {string} currentQuery - the current URL's query string
 * @returns {boolean}
 */
export const isActive = (chipQuery, currentQuery) => {
  const chipParams = [...new URLSearchParams(chipQuery)];
  const current = new URLSearchParams(currentQuery);

  return (
    chipParams.length > 0 && chipParams.every(([key, value]) => current.getAll(key).includes(value))
  );
};

/**
 * The query string a chip click should leave behind: its params applied, or
 * removed again when the chip was already active (click to toggle off).
 *
 * `page` always goes: the customer is on page 3 of the old result set, and
 * there is no reason to believe the new one has a page 3.
 *
 * @param {string} chipQuery - the chip's own query string
 * @param {string} currentQuery - the current URL's query string
 * @returns {string} query string, without the leading `?`
 */
export const nextQuery = (chipQuery, currentQuery) => {
  const chipParams = [...new URLSearchParams(chipQuery)];
  const clearing = isActive(chipQuery, currentQuery);
  const next = new URLSearchParams();

  const dropped = ([key, value]) =>
    chipParams.some(([chipKey, chipValue]) => {
      if (PRICE_BOUND.test(chipKey)) return PRICE_BOUND.test(key);
      if (SINGLE_VALUED.test(chipKey)) return chipKey === key;
      return chipKey === key && chipValue === value;
    });

  for (const param of new URLSearchParams(currentQuery)) {
    if (param[0] === 'page' || dropped(param)) continue;
    next.append(...param);
  }

  if (!clearing) {
    for (const [key, value] of chipParams) next.append(key, value);
  }

  return next.toString();
};
